/**
 * Parses a date string (ISO 8601, Unix timestamp, or any standard format)
 * and returns an object with multiple named format variants.
 *
 * Usage: const f = parseDate(att.uploaded_at); f.dmyTime => "05-01-2026 2:36 AM"
 */
export const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;

    const dd   = String(d.getDate()).padStart(2, '0');
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const yy   = String(yyyy).slice(-2);

    const h24  = d.getHours();
    const h12  = h24 % 12 || 12;
    const min  = String(d.getMinutes()).padStart(2, '0');
    const sec  = String(d.getSeconds()).padStart(2, '0');
    const ampm = h24 >= 12 ? 'PM' : 'AM';

    const monthNames  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthFull   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const mon  = monthNames[d.getMonth()];
    const month = monthFull[d.getMonth()];

    return {
        // Date-only formats
        dmy:        `${dd}/${mm}/${yyyy}`,           // 05/01/2026
        dmyDash:    `${dd}-${mm}-${yyyy}`,           // 05-01-2026
        mdy:        `${mm}/${dd}/${yyyy}`,           // 01/05/2026
        ymd:        `${yyyy}-${mm}-${dd}`,           // 2026-01-05  (ISO date)
        dMonY:      `${dd} ${mon} ${yyyy}`,          // 05 Jan 2026
        dMonthY:    `${dd} ${month} ${yyyy}`,        // 05 January 2026
        monDY:      `${mon} ${dd}, ${yyyy}`,         // Jan 05, 2026
        monthDY:    `${month} ${dd}, ${yyyy}`,       // January 05, 2026

        // Time-only formats
        time24:     `${String(h24).padStart(2,'0')}:${min}`,          // 14:36
        time24s:    `${String(h24).padStart(2,'0')}:${min}:${sec}`,   // 14:36:00
        time12:     `${h12}:${min} ${ampm}`,                          // 2:36 PM
        time12s:    `${h12}:${min}:${sec} ${ampm}`,                   // 2:36:00 PM

        // Date + time combos
        dmyTime:    `${dd}-${mm}-${yyyy} ${h12}:${min} ${ampm}`,     // 05-01-2026 2:36 AM
        dmyTime24:  `${dd}-${mm}-${yyyy} ${String(h24).padStart(2,'0')}:${min}`, // 05-01-2026 14:36
        dmySlashTime: `${dd}/${mm}/${yyyy} ${h12}:${min} ${ampm}`,   // 05/01/2026 2:36 AM
        iso:        d.toISOString(),                                   // 2026-01-05T14:36:00.000Z
        relative:   null, // filled by formatTimeAgo if needed
    };
};

export const formatTimeAgo = (dateString, locale = 'en') => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) {
        return locale === 'ar' ? `قبل ${interval} سنة` : `${interval} year${interval > 1 ? 's' : ''} ago`;
    }
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
        return locale === 'ar' ? `قبل ${interval} شهر` : `${interval} month${interval > 1 ? 's' : ''} ago`;
    }
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) {
        return locale === 'ar' ? `قبل ${interval} يوم` : `${interval} day${interval > 1 ? 's' : ''} ago`;
    }
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) {
        return locale === 'ar' ? `قبل ${interval} ساعة` : `${interval} hour${interval > 1 ? 's' : ''} ago`;
    }
    interval = Math.floor(seconds / 60);
    if (interval >= 1) {
        return locale === 'ar' ? `قبل ${interval} دقيقة` : `${interval} minute${interval > 1 ? 's' : ''} ago`;
    }
    return locale === 'ar' ? 'الآن' : 'just now';
};
